<template>
  <v-card class="pa-4 mb-4 rounded-lg elevation-2 position-relative">
    <v-overlay v-if="loader" absolute color="white" opacity="0.85">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
        <div class="mt-2 subtitle-2 primary--text font-weight-bold">Cargando detalles...</div>
      </div>
    </v-overlay>

    <!-- Cabecera principal -->
    <v-card-title class="px-0 pb-4 pt-2 d-flex justify-space-between align-center">
      <div class="d-flex align-center">
        <v-icon large color="primary" class="mr-3">mdi-file-document-outline</v-icon>
        <div>
          <span class="text-h6 font-weight-bold">Detalle de Cuenta por Cobrar</span>
          <div class="text-caption grey--text">Visualiza y gestiona el historial de consumos del cliente</div>
        </div>
      </div>

      <!-- Acciones de Exportación -->
      <div class="d-none d-sm-flex">
        <v-btn depressed color="success" @click="exportExcel" class="mr-2 text-none font-weight-bold">
          <v-icon left>mdi-file-excel</v-icon>Exportar a Excel
        </v-btn>
        <v-btn depressed color="error" @click="generatePdf" class="text-none font-weight-bold">
          <v-icon left>mdi-file-pdf-box</v-icon>Exportar PDF
        </v-btn>
      </div>
    </v-card-title>

    <!-- Resumen del Cliente y Balance (Tarjetas KPI) -->
    <v-row class="mb-4">
      <v-col cols="12" md="6" class="py-1">
        <v-card outlined class="rounded-lg border-subtle d-flex align-center pa-4 fill-height">
          <v-avatar color="primary lighten-5" size="56" class="mr-4">
            <v-icon large color="primary">mdi-account-outline</v-icon>
          </v-avatar>
          <div class="flex-grow-1" style="min-width: 0;">
            <div class="text-caption grey--text font-weight-medium">Cliente</div>
            <div class="text-h6 font-weight-bold text-truncate">
              {{ cuenta.clienteData.Nombres }} {{ cuenta.clienteData.Apellidos }}
            </div>
            <div class="text-caption grey--text">ID Cliente: {{ cuenta.Cliente }}</div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6" class="py-1">
        <v-card outlined class="rounded-lg border-subtle d-flex align-center pa-4 fill-height">
          <v-avatar color="success lighten-5" size="56" class="mr-4">
            <v-icon large color="success">mdi-currency-usd</v-icon>
          </v-avatar>
          <div>
            <div class="text-caption grey--text font-weight-medium">Monto Pendiente</div>
            <div class="text-h5 font-weight-black success--text">
              ${{ $pgsHelper.formatNumber(totalGeneral) }}
              <span class="text-caption grey--text font-weight-medium">
                {{ cuenta.Totales.MXN ? $Currencies.getCurrencyDefault() : "USD" }}
              </span>
            </div>
            <div class="text-caption grey--text">Monto calculado de consumos actuales</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Acciones móviles (se muestran solo en pantallas pequeñas) -->
    <v-row class="d-flex d-sm-none mb-4 px-3">
      <v-col cols="6" class="pa-1">
        <v-btn depressed block color="success" @click="exportExcel" class="text-none small">
          <v-icon left>mdi-file-excel</v-icon>Excel
        </v-btn>
      </v-col>
      <v-col cols="6" class="pa-1">
        <v-btn depressed block color="error" @click="generatePdf" class="text-none small">
          <v-icon left>mdi-file-pdf-box</v-icon>PDF
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla de Productos -->
    <v-card outlined class="rounded-lg border-subtle overflow-hidden">
      <v-card-title class="px-4 py-3 grey lighten-5 d-flex justify-space-between align-center">
        <span class="text-subtitle-1 font-weight-bold">Detalle de Consumos</span>
        <v-btn color="red" text small class="text-none font-weight-bold" @click="deleteProducto('all')">
          <v-icon left small>mdi-delete-sweep-outline</v-icon>Eliminar todo
        </v-btn>
      </v-card-title>

      <v-simple-table>
        <thead>
          <tr class="grey lighten-4">
            <th class="text-left font-weight-bold grey--text text--darken-3">Producto</th>
            <th class="text-center font-weight-bold grey--text text--darken-3">Cantidad</th>
            <th class="text-right font-weight-bold grey--text text--darken-3">Precio Unitario</th>
            <th class="text-right font-weight-bold grey--text text--darken-3">Importe</th>
            <th class="text-center font-weight-bold grey--text text--darken-3">Fecha de Cargo</th>
            <th class="text-center font-weight-bold grey--text text--darken-3" style="width: 80px;">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!cuenta.Catalogo || !cuenta.Catalogo.length">
            <td colspan="6" class="text-center py-8 grey--text">
              <v-icon size="48" color="grey lighten-1">mdi-package-variant</v-icon>
              <div class="mt-2 text-subtitle-2">No hay consumos registrados en esta cuenta</div>
            </td>
          </tr>

          <template v-else v-for="(agrupador, index) in calatalogo">
            <!-- Encabezado de Agrupador por Fecha -->
            <tr :key="'group-' + index" class="grey lighten-4 font-weight-medium">
              <td colspan="3" class="py-2 pl-4">
                <v-chip small color="primary" class="font-weight-bold mr-2">
                  <v-icon left small>mdi-calendar</v-icon>Fecha
                </v-chip>
                <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">{{ index }}</span>
              </td>
              <td colspan="3" class="text-right py-2 pr-4">
                <v-btn color="red" text small class="text-none font-weight-bold"
                  @click="deleteProducto('fecha', agrupador)">
                  <v-icon left small>mdi-delete-outline</v-icon>Eliminar grupo
                </v-btn>
              </td>
            </tr>

            <!-- Productos del grupo -->
            <tr v-for="prod in agrupador" :key="prod.ExtraParams && prod.ExtraParams.CuentaData ? prod.ExtraParams.CuentaData.CPI : Math.random()" class="hover-row">
              <td class="font-weight-medium">
                {{ prod.Nombre }}
                <template v-if="prod.ExtraParams && prod.ExtraParams.CuentaData && prod.ExtraParams.CuentaData.Folio">
                  <br />
                  <span class="font-weight-bold grey--text text--darken-3">
                    Folio: {{ prod.ExtraParams.CuentaData.Folio }}
                  </span>
                </template>
              </td>
              <td class="text-center">{{ prod.Cnt }}</td>
              <td class="text-right font-weight-medium">
                ${{ $pgsHelper.formatNumber(prod.Pco ? prod.Pco.C : 0) }} <span class="text-caption grey--text">{{ prod.Pco ? prod.Pco.M : '' }}</span>
              </td>
              <td class="text-right font-weight-bold success--text">
                ${{ $pgsHelper.formatNumber((prod.Pco ? prod.Pco.C : 0) * (prod.Cnt ?? 0)) }} <span class="text-caption grey--text">{{
                  prod.Pco ? prod.Pco.M : '' }}</span>
              </td>
              <td class="text-center grey--text text--darken-1">{{ prod.fecha }}</td>
              <td class="text-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon color="red" v-bind="attrs" v-on="on" @click="deleteProducto('prod', prod)" small>
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Eliminar cargo</span>
                </v-tooltip>
              </td>
            </tr>
          </template>

          <!-- Fila de Total General -->
          <tr v-if="cuenta.Catalogo && cuenta.Catalogo.length"
            class="green lighten-5 font-weight-black text-subtitle-1">
            <td colspan="3" class="text-right pr-4 font-weight-bold grey--text text--darken-3">Total de la Cuenta:</td>
            <td class="text-right font-weight-black success--text pr-4">
              ${{ $pgsHelper.formatNumber(totalGeneral) }}
              <span class="text-caption font-weight-medium grey--text text--darken-2">
                {{ $Currencies.getCurrencyDefault() }}
              </span>
            </td>
            <td colspan="2"></td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>
  </v-card>
</template>

<style scoped>
.hover-row:hover {
  background-color: #fafafa;
}
</style>

<script>
import moment from "moment";
import DetalleCXCExporter from "../../../clases/ExcelMaker/DetalleCXC.js";
import CxcDetalle from "../../../clases/PdfMakers/CxcDetalle";
import Currency from "../../../clases/Currencies.js";
import pdfmake from "pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfmake.vfs = pdfFonts.pdfMake.vfs;

export default {
  name: "CuentaEdit",
  components: {},
  props: ["edit", "dataEdit"],
  data() {
    return {
      cuenta: {
        clienteData: {},
        Totales: {},
      },
      Id: "",
      errors: {},
      loader: true,
      dataSuccess: [],
      keycalatalogo: 1,
    };
  },
  methods: {
    getDateFormat(data) {
      return moment(data);
    },
    checkForm: function (e) {
      e.preventDefault();
      let valid = true;

      this.errors = [];

      if (valid) {
        let doAction = "CREATE";
        if (this.edit) {
          doAction = "EDIT";
        }
        this.$emit("successData", this.$data, doAction);
      }
    },

    deleteProducto(filtro, prod) {
      const productos =
        filtro === "all"
          ? this.cuenta.Catalogo
          : filtro === "fecha" && Array.isArray(prod)
            ? prod
            : filtro === "prod"
              ? [prod]
              : [];

      if (!productos.length) return;

      const dataSend = {
        IdCliente: this.cuenta.Cliente,
        Productos: productos.map((item) => ({
          CPI: item.ExtraParams && item.ExtraParams.CuentaData ? Number(item.ExtraParams.CuentaData.CPI) : null,
        })),
      };

      const confirmMessage =
        filtro == "prod"
          ? "definitivamente este producto"
          : filtro == "fecha"
            ? "definitivamente los productos"
            : "definitivamente TODOS los productos";

      // console.log("Productos a eliminar:", dataSend);
      let stringProdcutos = ""
      productos.forEach(function (item) {
        stringProdcutos += `"${item.Nombre} (x${item.Cnt})",`
      })
      this.$alert.alertConfirm({
        action: "eliminar",
        obj: confirmMessage,
        route: "PDV-CXC-eliminarProductos",
        data: dataSend,
        onSuccess: () => {
          this.$pgsHelper.writeLog({
            Evento: "DELETE_CXC_ITEM",
            Mensaje: `Productos eliminados de cuenta cxc del cliente ${this.cuenta.clienteData.Nombres} ${this.cuenta.clienteData.Apellidos} con los productos: ${stringProdcutos}`,
            reference: { id: this.cuenta.id, tp: "CUENTA_CXC", al: "CUENTA DE CXC" },
            data: productos,
          });
          this.$emit("reload");
        },
      });
    },

    exportExcel() {
      const moneda = this.$Currencies.getCurrencyDefault();
      DetalleCXCExporter.generateExcelBuffer(this.calatalogo, moneda)
        .then((buffer) => {
          const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "Cuentas por cobrar.xlsx";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => console.error("Error exportando Excel:", err));
    },

    async generatePdf() {
      let mainData = {
        TITLE:
          "CXC del cliente " +
          this.cuenta.clienteData.Nombres +
          " " +
          this.cuenta.clienteData.Apellidos,
        FG: new moment().format("DD/MM/YYYY"),
        HG: new moment().format("HH:mm:ss"),
        data: this.calatalogo,
        currency: Currency.getCurrencyDefault(),
        monto: this.cuenta.Totales.MXN
          ? this.cuenta.Totales.MXN
          : this.cuenta.Totales.USD,
      };
      let docData = CxcDetalle.getDocData(mainData);
      pdfmake.createPdf(docData).open();
    },
  },
  computed: {
    calatalogo() {
      const key = this.keycalatalogo;
      if (!this.cuenta.Catalogo || !this.cuenta.Catalogo.length) {
        return {};
      }
      this.cuenta.Catalogo.forEach((element) => {
        element.fecha = element.ExtraParams && element.ExtraParams.CuentaData && element.ExtraParams.CuentaData.Crat
          ? this.getDateFormat(element.ExtraParams.CuentaData.Crat).format(
            "YYYY-MM-DD HH:mm"
          )
          : "Otros";
      });
      let agrupador = {};
      this.cuenta.Catalogo.forEach((element) => {
        if (!agrupador[element.fecha]) {
          agrupador[element.fecha] = [];
        }
        agrupador[element.fecha].push(element);
      });

      return agrupador;
    },

    totalGeneral() {
      if (!this.cuenta.Catalogo || !this.cuenta.Catalogo.length) return 0;
      return this.cuenta.Catalogo.reduce((sum, prod) => {
        const precio = prod.Pco?.C ?? 0;
        const cantidad = prod.Cnt ?? 0;
        return sum + precio * cantidad;
      }, 0);
    },
  },
  watch: {},
  beforeMount: function () {
    this.loader = true;
  },
  mounted() {
    this.loader = false;

    if (this.edit) {
      this.Id = this.dataEdit.id;
      this.cuenta = this.dataEdit;
    }
  },
};
</script>
